import type { ShopifyOfferDto, PricingMethodKey, DiscountBreakdown, InstallmentInfo } from '../offer.types';

/**
 * Leitura do preço canônico (Etapa 2). FONTE ÚNICA — lê apenas `offer.pricing`.
 * Sem fallback para campos legados (price/prices/discount_breakdown): rodamos o
 * sistema novo puro. Se `pricing` não chegar, o valor sai 0 (sinaliza erro na hora).
 */

type OfferLike = Pick<ShopifyOfferDto, 'pricing'>;

/** O que o cliente paga (método escolhido/melhor, com bump). */
export function getOfferTotal(offer: OfferLike | null | undefined): number {
  return offer?.pricing?.total ?? 0;
}

/** Soma dos produtos antes de descontos (subtotal). */
export function getOfferSubtotal(offer: OfferLike | null | undefined): number {
  return offer?.pricing?.productsSubtotal ?? 0;
}

/** Total de descontos aplicados. */
export function getOfferDiscount(offer: OfferLike | null | undefined): number {
  return offer?.pricing?.totalDiscounts ?? 0;
}

/** Preço final de um método de pagamento específico. */
export function getMethodPrice(offer: OfferLike | null | undefined, method: PricingMethodKey): number | undefined {
  return offer?.pricing?.byMethod?.[method]?.total;
}

const METHOD_LABEL: Record<string, string> = {
  card: 'CARD',
  pix: 'PIX',
  billet: 'BOLETO',
  apple_pay: 'APPLE_PAY',
  google_pay: 'GOOGLE_PAY',
};

/**
 * Deriva o breakdown de descontos (forma legada, usada pelo resumo) a partir do `pricing`.
 * Mantém o resumo do pedido 100% na fonte única, sem ler campos legados do offer.
 */
export function toDiscountBreakdown(offer: OfferLike | null | undefined): DiscountBreakdown | undefined {
  const p = offer?.pricing;
  if (!p) return undefined;
  return {
    basePrice: p.productsSubtotal,
    couponDiscount: p.couponDiscount,
    paymentMethodDiscount: p.paymentMethodDiscount,
    progressiveDiscount: p.progressiveDiscount,
    totalDiscount: p.totalDiscounts,
    finalPrice: p.total,
    paymentMethod: p.paymentMethod ? METHOD_LABEL[p.paymentMethod] : undefined,
    orderBumpTotal: p.orderBumpTotal,
  };
}

/** Deriva o parcelamento (forma legada do resumo) a partir do `pricing` (slot do cartão = método selecionado). */
export function toInstallmentInfo(offer: OfferLike | null | undefined): InstallmentInfo | undefined {
  const inst = offer?.pricing?.byMethod?.card?.installments;
  if (!inst) return undefined;
  return {
    installments: inst.installments,
    installment_amount: inst.installment_amount,
    cash_price: inst.cash_price,
    has_interest: inst.has_interest,
    available_installments: inst.available_installments,
  };
}
